/**
 * In-app viewer for Mail-Away attachments stored as order files.
 * Fetches GET /orders/{id}/files/{fileId}/download as a blob; PDFs/images render
 * inline, other types fall back to a download button.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, ExternalLink, Loader2, Paperclip } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

export const MailAwayAttachmentViewer = ({ open, onOpenChange, orderId, attachment }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [type, setType] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !orderId || !attachment?.file_id) return undefined;
    let url = null;
    let cancelled = false;
    setLoading(true); setError(''); setBlobUrl(null);
    axios.get(`${API}/api/orders/${orderId}/files/${attachment.file_id}/download`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setType(res.data.type || '');
        setBlobUrl(url);
      })
      .catch(() => { if (!cancelled) setError('Unable to load this attachment.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [open, orderId, attachment?.file_id]);

  const inline = type === 'application/pdf' || type.startsWith('image/');
  const name = attachment?.filename || 'Attachment';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl w-[calc(100vw-2rem)] h-[85vh] flex flex-col gap-3 p-4" data-testid="mailaway-attachment-viewer">
        <div className="flex items-center justify-between gap-3 pr-6">
          <div className="min-w-0">
            <DialogTitle className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-slate-400" />{name}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">{attachment?.kind === 'response' ? 'Response document' : 'Request attachment'}</DialogDescription>
          </div>
          {blobUrl && (
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => window.open(blobUrl, '_blank')} data-testid="mailaway-attachment-open-new-tab">
                <ExternalLink className="h-3.5 w-3.5 mr-1" />Open
              </Button>
              <a href={blobUrl} download={name} className="inline-flex items-center h-8 px-3 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700" data-testid="mailaway-attachment-download">
                <Download className="h-3.5 w-3.5 mr-1" />Download
              </a>
            </div>
          )}
        </div>
        <div className="flex-1 min-h-0 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
          {loading && <Loader2 className="h-6 w-6 animate-spin text-slate-400" />}
          {!loading && error && <p className="text-sm text-red-600" data-testid="mailaway-attachment-error">{error}</p>}
          {!loading && blobUrl && inline && (
            type === 'application/pdf'
              ? <iframe title={name} src={blobUrl} className="w-full h-full" data-testid="mailaway-attachment-frame" />
              : <img alt={name} src={blobUrl} className="max-w-full max-h-full object-contain" data-testid="mailaway-attachment-image" />
          )}
          {!loading && blobUrl && !inline && (
            <p className="text-sm text-slate-500">Preview not available for this file type — use Download.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MailAwayAttachmentViewer;
